import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Card, 
  Title, 
  Grid,
  Text,
  Metric,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Select,
  SelectItem,
  Badge,
} from '@tremor/react';
import EnhancedTable from '../../components/common/EnhancedTable';
import '../../components/common/EnhancedTable/EnhancedTableFix.css';
import { supabase } from '../../lib/supabase';
import type { DevFinancial } from '../../types/financial';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';

type SortField = 'community' | 'date' | 'income' | 'expense' | 'description' | 'project' | 'supplier';

export default function FinancialsPage() {
  const [sortField, setSortField] = React.useState<SortField>('date');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  type FinancialYear = 'all' | string; // e.g. '2024-2025'
  
  const [filters, setFilters] = React.useState({
    community: '',
    description: '',
    supplier: '',
    financialYear: 'all' as FinancialYear
  });

  // Generate financial year options
  const generateFinancialYearOptions = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // If we're in the first half of the calendar year, the current FY started last year
    const currentFY = currentMonth < 6 ? currentYear - 1 : currentYear;
    
    const options = [
      { value: 'all', label: 'All Time' }
    ];

    // Generate last 5 financial years
    for (let i = 0; i < 5; i++) {
      const startYear = currentFY - i;
      const endYear = startYear + 1;
      options.push({
        value: `${startYear}-${endYear}`,
        label: `FY ${startYear}-${endYear}`
      });
    }

    return options;
  };

  const financialYearOptions = generateFinancialYearOptions();

  // Get date range for financial year
  const getDateRangeForFinancialYear = (fy: FinancialYear): { from?: string; to?: string } => {
    if (fy === 'all') return {};
    
    const [startYear, endYear] = fy.split('-').map(Number);
    
    // Validate years
    if (isNaN(startYear) || isNaN(endYear)) {
      console.error('Invalid financial year format:', fy);
      return {};
    }
    
    // Create dates for financial year (July 1st to June 30th)
    const from = new Date(Date.UTC(startYear, 6, 1)); // July 1st
    const to = new Date(Date.UTC(endYear, 5, 30)); // June 30th
    
    return {
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0]
    };
  };

  const sortData = (data: DevFinancial[]) => {
    return [...data].sort((a, b) => {
      if (sortDirection === 'desc') [a, b] = [b, a];
      switch (sortField) {
        case 'community': return (a.fin_comm || '').localeCompare(b.fin_comm || '');
        case 'date': return (a.fin_date || '').localeCompare(b.fin_date || '');
        case 'income': return (a.fin_inc_amt || 0) - (b.fin_inc_amt || 0);
        case 'expense': return (a.fin_exp_amt || 0) - (b.fin_exp_amt || 0);
        case 'description': return (a.fin_desc || '').localeCompare(b.fin_desc || '');
        case 'project': return (a.fin_exp_proj || '').localeCompare(b.fin_exp_proj || '');
        case 'supplier': return (a.fin_supplier || '').localeCompare(b.fin_supplier || '');
        default: return 0;
      }
    });
  };

  const filterData = (data: DevFinancial[]) => {
    return data.filter(item => {
      const matchesCommunity = !filters.community || item.fin_comm === filters.community;
      const matchesDescription = !filters.description || item.fin_desc?.toLowerCase().includes(filters.description.toLowerCase());
      const matchesSupplier = !filters.supplier || item.fin_supplier === filters.supplier;
      
      // Apply financial year filter
      let matchesFinancialYear = true;
      if (filters.financialYear !== 'all') {
        const { from, to } = getDateRangeForFinancialYear(filters.financialYear);
        if (from && to) {
          const itemDate = item.fin_date;
          matchesFinancialYear = itemDate >= from && itemDate <= to;
        }
      }
      
      return matchesCommunity && matchesDescription && matchesSupplier && matchesFinancialYear;
    });
  };

  const handleSort = (field: SortField) => {
    setSortDirection(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };
  const { data: financials = [], isLoading, error } = useQuery<DevFinancial[]>({
    queryKey: ['financials'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('dev_financials')
          .select('*');

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        return data || [];
      } catch (error) {
        console.error('Error fetching financial data:', error);
        throw error;
      }
    }
  });

  const communities = React.useMemo(() => 
    Array.from(new Set(financials.map((f: DevFinancial) => f.fin_comm))),
    [financials]
  );


  const summaryStats = React.useMemo(() => {
    // Apply financial year filter to data before calculating stats
    const filteredFinancials = filterData(financials);
    
    const totalIncome = filteredFinancials.reduce((sum: number, curr: DevFinancial) => sum + (curr.fin_inc_amt || 0), 0);
    const avgIncomePerCommunity = totalIncome / (communities.length || 1);
    const totalExpenditure = filteredFinancials.reduce((sum: number, curr: DevFinancial) => sum + (curr.fin_exp_amt || 0), 0);
    const avgExpPerCommunity = totalExpenditure / (communities.length || 1);

    return {
      totalIncome,
      avgIncomePerCommunity,
      totalExpenditure,
      avgExpPerCommunity
    };
  }, [financials, communities.length, filters]); // Add filters as a dependency

  const chartData = React.useMemo(() => {
    // Apply financial year filter to data before calculating chart data
    const filteredFinancials = filterData(financials);
    
    const communityTotals = filteredFinancials.reduce((acc: { community: string; amount: number; transactions: number }[], curr: DevFinancial) => {
      const existing = acc.find(item => item.community === curr.fin_comm);
      if (existing) {
        existing.amount += curr.fin_inc_amt;
        existing.transactions += 1;
      } else {
        acc.push({ 
          community: curr.fin_comm, 
          amount: curr.fin_inc_amt,
          transactions: 1
        });
      }
      return acc;
    }, []);

    return communityTotals.sort((a, b) => b.amount - a.amount);
  }, [financials, filters]); // Add filters as a dependency

  const formatCurrency = (value: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatTableDate = (dateString: string) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) return dateString;
    return `${day}/${month}/${year}`;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          <h3 className="font-semibold">Error loading financial data</h3>
          <p className="text-sm mt-1">
            {error instanceof Error ? error.message : 'An unknown error occurred'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Financial Analysis</h1>
        <p className="mt-1 text-sm text-gray-500">Financial income analysis by community</p>
      </div>

      {/* Combined Income and Expenditure Statistics */}
      <Card className="mb-4 p-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="border-l-4 border-emerald-500 pl-2">
            <Text className="text-xs text-gray-600">Total Income</Text>
            <Metric className="text-lg text-emerald-600">{formatCurrency(summaryStats.totalIncome)}</Metric>
          </div>
          <div className="border-l-4 border-emerald-500 pl-2">
            <Text className="text-xs text-gray-600">Avg Income per Community</Text>
            <Metric className="text-lg text-emerald-600">{formatCurrency(summaryStats.avgIncomePerCommunity)}</Metric>
          </div>
          <div className="border-l-4 border-red-500 pl-2">
            <Text className="text-xs text-gray-600">Total Expenditure</Text>
            <Metric className="text-lg text-red-600">{formatCurrency(summaryStats.totalExpenditure)}</Metric>
          </div>
          <div className="border-l-4 border-red-500 pl-2">
            <Text className="text-xs text-gray-600">Avg Expenditure per Community</Text>
            <Metric className="text-lg text-red-600">{formatCurrency(summaryStats.avgExpPerCommunity)}</Metric>
          </div>
        </div>
      </Card>

      {/* Financial Data Table */}
      <Card className="overflow-visible">
        <Title>Financial Transactions</Title>
        
        {/* Financial Year Filter */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <Text className="font-medium">Financial Year</Text>
            {filters.financialYear !== 'all' && (
              <Badge size="sm" color="orange">
                {financialYearOptions.find(opt => opt.value === filters.financialYear)?.label}
              </Badge>
            )}
          </div>
          <Select
            value={filters.financialYear}
            onValueChange={(value) => setFilters(prev => ({ ...prev, financialYear: value as FinancialYear }))}
            placeholder="Select Financial Year"
            className="max-w-xs mb-4"
          >
            {financialYearOptions.map(option => (
              <SelectItem
                key={option.value}
                value={option.value}
              >
                {option.label}
              </SelectItem>
            ))}
          </Select>
        </div>
        
        {/* Other Filters */}
        <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 w-full">
            <select 
              value={filters.community}
              onChange={(e) => setFilters(prev => ({ ...prev, community: e.target.value }))}
              className="border rounded p-2 text-sm w-full"
            >
              <option value="">All Communities</option>
              {communities.map(community => (
                <option key={community} value={community}>{community}</option>
              ))}
            </select>
            <input
              type="text"
              value={filters.description}
              onChange={(e) => setFilters(prev => ({ ...prev, description: e.target.value }))}
              className="border rounded p-2 text-sm w-full"
              placeholder="Filter by Description"
            />
            <select 
              value={filters.supplier}
              onChange={(e) => setFilters(prev => ({ ...prev, supplier: e.target.value }))}
              className="border rounded p-2 text-sm w-full"
            >
              <option value="">All Suppliers</option>
              {Array.from(new Set(financials.map(f => f.fin_supplier).filter(Boolean))).map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Clear Filters Button */}
        <div className="mb-4">
          <button
            onClick={() => setFilters({ community: '', description: '', supplier: '', financialYear: 'all' })}
            className="border rounded px-3 py-2 bg-gray-100 hover:bg-gray-200 text-sm"
          >
            Clear All Filters
          </button>
        </div>
        {/* Summary Row */}
        <div className="bg-gray-50 p-4 mb-4 rounded-lg border border-gray-200">
          {(() => {
            const filteredData = sortData(filterData(financials));
            const totalIncome = filteredData.reduce((sum, curr) => sum + (curr.fin_inc_amt || 0), 0);
            const totalExpense = filteredData.reduce((sum, curr) => sum + (curr.fin_exp_amt || 0), 0);
            return (
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 text-left">
                  <Text className="text-gray-600 text-sm">Records Found</Text>
                  <Metric className="text-base sm:text-lg block mt-1">{filteredData.length}</Metric>
                </div>
                <div className="flex-1 text-left border-t sm:border-t-0 pt-4 sm:pt-0">
                  <Text className="text-gray-600 text-sm">Total Income</Text>
                  <Metric className="text-base sm:text-lg block mt-1 text-emerald-600">{formatCurrency(totalIncome)}</Metric>
                </div>
                <div className="flex-1 text-left border-t sm:border-t-0 pt-4 sm:pt-0">
                  <Text className="text-gray-600 text-sm">Total Expenses</Text>
                  <Metric className="text-base sm:text-lg block mt-1 text-red-600">{formatCurrency(totalExpense)}</Metric>
                </div>
              </div>
            );
          })()}
        </div>
        
        <EnhancedTable
          columns={[
            { 
              id: 'community', 
              header: (
                <button onClick={() => handleSort('community')} className="flex items-center gap-1">
                  Community {getSortIcon('community')}
                </button>
              ),
              accessor: (row) => (
                <span className="text-sm text-gray-900 font-medium">{row.fin_comm}</span>
              ),
              width: 'medium'
            },
            { 
              id: 'date', 
              header: (
                <button onClick={() => handleSort('date')} className="flex items-center gap-1">
                  Date {getSortIcon('date')}
                </button>
              ),
              accessor: (row) => (
                <span className="text-sm text-gray-500">{formatTableDate(row.fin_date)}</span>
              ),
              width: 'medium'
            },
            { 
              id: 'income', 
              header: (
                <button onClick={() => handleSort('income')} className="flex items-center gap-1">
                  Income {getSortIcon('income')}
                </button>
              ),
              accessor: (row) => (
                <span className="text-sm text-gray-500">{formatCurrency(row.fin_inc_amt)}</span>
              ),
              width: 'medium',
              align: 'right'
            },
            { 
              id: 'expense', 
              header: (
                <button onClick={() => handleSort('expense')} className="flex items-center gap-1">
                  Expense {getSortIcon('expense')}
                </button>
              ),
              accessor: (row) => (
                <span className="text-sm text-gray-500">{formatCurrency(row.fin_exp_amt)}</span>
              ),
              width: 'medium',
              align: 'right'
            },
            { 
              id: 'description', 
              header: (
                <button onClick={() => handleSort('description')} className="flex items-center gap-1">
                  Description {getSortIcon('description')}
                </button>
              ),
              accessor: (row) => (
                <div className="truncate max-w-[200px] text-sm text-gray-500" title={row.fin_desc}>
                  {row.fin_desc || 'Not specified'}
                </div>
              ),
              width: 'wide'
            },
            { 
              id: 'project', 
              header: (
                <button onClick={() => handleSort('project')} className="flex items-center gap-1">
                  Project {getSortIcon('project')}
                </button>
              ),
              accessor: (row) => (
                <span className="text-sm text-gray-500">{row.fin_exp_proj || 'Not specified'}</span>
              ),
              width: 'medium'
            },
            { 
              id: 'supplier', 
              header: (
                <button onClick={() => handleSort('supplier')} className="flex items-center gap-1">
                  Supplier {getSortIcon('supplier')}
                </button>
              ),
              accessor: (row) => (
                <span className="text-sm text-gray-500">{row.fin_supplier || 'Not specified'}</span>
              ),
              width: 'medium'
            }
          ]}
          data={sortData(filterData(financials))}
          keyField="fin_id"
          width="1400px"
          maxHeight="calc(100vh - 24rem)"
          emptyMessage="No financial records found"
          horizontalScroll={true}
          className="enhanced-table-force-scroll"
        />
      </Card>
    </div>
  );
}
